'use client';

import { useEffect, useState } from 'react';
import { RightRailArticleAd } from '@/app_components/ads/RightRailArticleAd';
import { RightRailFlex } from '@/app_components/ads/RightRailFlex';
import { AbTest } from '@/app_components/common/AbTest';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import { usePaywall } from '@/app_lib/providers/PaywallProvider';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

interface RightRailWrapperProps {
  noInstream?: boolean;
}

export const RightRail = ({ noInstream = false }: RightRailWrapperProps) => {
  const { userFlags: userVariants } = useOptimizely();
  const isDesktop = useMediaQuery(Breakpoints.Desktop);
  const {
    paywallState: { paywallCheck },
  } = usePaywall();

  const [stickyRightRailOffset, setStickyRightRailOffset] = useState(0);

  const rightRailVariants = (userVariants?.desktop_right_rail as { variant: string }) || { variant: 'off' };
  const renderRightRails = rightRailVariants?.variant === 'r2' || rightRailVariants?.variant === 'r4';
  const renderStickyRightRail = rightRailVariants?.variant === 'r4';

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const articleContentElement = document.querySelector('.container-content');
    const articleContentWrapperElement = document.querySelector('.container-content-wrapper');

    // Sets the offset of the large sticky right rail ad to place below the first instream ad
    const updateOffset = () => {
      if (!(renderRightRails && isDesktop)) {
        return;
      }

      if (paywallCheck === 'paywalled' || noInstream) {
        setStickyRightRailOffset(36); // Default gap when there is no instream ad
        return;
      }

      const thirdParagraph = articleContentWrapperElement?.querySelector('p:nth-of-type(2)');

      const articleContentGap = 64; // Gap for the article content (layout-gap-default)
      const firstInStreamAdHeight = 250; // Max height of the first instream ad for desktops
      const staticRightRailHeight = 250; // Added to offset to account for the static Right Rail Ad

      if (thirdParagraph) {
        const thirdParagraphBottom = thirdParagraph.getBoundingClientRect().bottom;
        const articleContainerTop = articleContentElement?.getBoundingClientRect().top || 0;
        const offset =
          thirdParagraphBottom -
          articleContainerTop +
          articleContentGap +
          firstInStreamAdHeight -
          staticRightRailHeight;
        setStickyRightRailOffset(offset);
      }
    };

    let observer: MutationObserver;
    if (articleContentWrapperElement) {
      observer = new MutationObserver(() => updateOffset());
      observer.observe(articleContentWrapperElement, { childList: true, subtree: true });
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [paywallCheck, noInstream, renderStickyRightRail, isDesktop]);

  if (!renderRightRails) {
    return null;
  }
  const rightRailFlex = (
    <RightRailFlex
      index={1}
      sticky={false}
    />
  );

  return (
    <div
      className={cn(
        'hidden w-[var(--max-rail-article-width)] h-full', // Max width for right rail article ad
        renderRightRails && 'xl:flex xl:flex-col xl:items-center',
      )}
      data-cy={ContentPlacement.ArticleRightRail}
    >
      <AbTest
        flagName='desktop_right_rail'
        variants={{ r2: rightRailFlex, r4: rightRailFlex }}
      />
      {stickyRightRailOffset !== 0 && (
        // Only render after offset is calculated to prevent Optimizely tracking issues

        <AbTest
          flagName='desktop_right_rail'
          variants={{
            r4: (
              <RightRailArticleAd
                index={1}
                marginTop={stickyRightRailOffset}
                sticky={true}
              />
            ),
          }}
        />
      )}
    </div>
  );
};
